import { clamp } from './math';

/** One sample of a pen stroke: position plus pen pressure (0..1). */
export type StrokePoint = [x: number, y: number, pressure: number];

/*
 * Stroke points are the bulk of a board document, so they are packed into a
 * compact binary form instead of being stored as number arrays:
 *
 *   [version u8] [flags u8] [count varuint] then per point:
 *   [dx zigzag-varint] [dy zigzag-varint] [pressure u8, if flagged]
 *
 * Coordinates are quantized to 0.1px and delta-encoded, so consecutive samples
 * of a hand-drawn line usually take 1 byte per axis. That is roughly 5-10x
 * smaller than letting Yjs store plain float arrays.
 */
const FORMAT_VERSION = 1;
const FLAG_PRESSURE = 1;
const SCALE = 10;

class ByteWriter {
  private buf: Uint8Array;
  private pos = 0;

  constructor(capacity: number) {
    this.buf = new Uint8Array(Math.max(16, capacity));
  }

  private ensure(extra: number): void {
    if (this.pos + extra <= this.buf.length) return;
    const next = new Uint8Array(Math.max(this.buf.length * 2, this.pos + extra));
    next.set(this.buf);
    this.buf = next;
  }

  byte(value: number): void {
    this.ensure(1);
    this.buf[this.pos++] = value & 255;
  }

  varuint(value: number): void {
    this.ensure(10);
    let v = value;
    while (v >= 0x80) {
      this.buf[this.pos++] = (v % 0x80) | 0x80;
      v = Math.floor(v / 0x80);
    }
    this.buf[this.pos++] = v;
  }

  varint(value: number): void {
    // ZigZag maps small negative numbers to small unsigned ones (0,-1,1,-2 -> 0,1,2,3).
    this.varuint(value >= 0 ? value * 2 : -value * 2 - 1);
  }

  finish(): Uint8Array {
    return this.buf.slice(0, this.pos);
  }
}

class ByteReader {
  private pos = 0;

  constructor(private readonly buf: Uint8Array) {}

  byte(): number {
    if (this.pos >= this.buf.length) throw new RangeError('Unexpected end of stroke data');
    return this.buf[this.pos++] as number;
  }

  varuint(): number {
    let result = 0;
    let multiplier = 1;
    for (;;) {
      const b = this.byte();
      result += (b & 0x7f) * multiplier;
      if (b < 0x80) return result;
      multiplier *= 0x80;
    }
  }

  varint(): number {
    const v = this.varuint();
    return v % 2 === 0 ? v / 2 : -(v + 1) / 2;
  }
}

export function encodePoints(points: readonly StrokePoint[], withPressure = true): Uint8Array {
  const out = new ByteWriter(points.length * (withPressure ? 3 : 2) + 8);
  out.byte(FORMAT_VERSION);
  out.byte(withPressure ? FLAG_PRESSURE : 0);
  out.varuint(points.length);
  let px = 0;
  let py = 0;
  for (const [x, y, pressure] of points) {
    const qx = Math.round(x * SCALE);
    const qy = Math.round(y * SCALE);
    out.varint(qx - px);
    out.varint(qy - py);
    px = qx;
    py = qy;
    if (withPressure) out.byte(Math.round(clamp(pressure, 0, 1) * 255));
  }
  return out.finish();
}

export function decodePoints(bytes: Uint8Array): StrokePoint[] {
  if (bytes.length === 0) return [];
  const reader = new ByteReader(bytes);
  const version = reader.byte();
  if (version !== FORMAT_VERSION) throw new Error(`Unsupported stroke format ${version}`);
  const hasPressure = (reader.byte() & FLAG_PRESSURE) !== 0;
  const count = reader.varuint();
  // Every point takes at least two bytes (three with pressure): a count the
  // data can't hold is corrupt, and must not size an array.
  if (count > (bytes.length - 2) / (hasPressure ? 3 : 2)) {
    throw new RangeError('Stroke data is shorter than its point count');
  }
  const points: StrokePoint[] = new Array(count);
  let qx = 0;
  let qy = 0;
  for (let i = 0; i < count; i++) {
    qx += reader.varint();
    qy += reader.varint();
    const pressure = hasPressure ? reader.byte() / 255 : 0.5;
    points[i] = [qx / SCALE, qy / SCALE, pressure];
  }
  return points;
}

const decodeCache = new WeakMap<Uint8Array, StrokePoint[]>();

/**
 * Decodes with a cache keyed by the byte array instance. Yjs hands back the same
 * Uint8Array for unchanged content, so re-rendering a stroke never re-decodes it.
 * The returned array must be treated as read-only.
 *
 * Corrupt data decodes to no points: a broken stroke is drawn as nothing,
 * rather than stopping everything around it from being drawn.
 */
export function decodePointsCached(bytes: Uint8Array): StrokePoint[] {
  let points = decodeCache.get(bytes);
  if (!points) {
    try {
      points = decodePoints(bytes);
    } catch {
      points = [];
    }
    decodeCache.set(bytes, points);
  }
  return points;
}

/**
 * Inserts samples so that no two consecutive points are more than `maxSpacing`
 * apart. Fast pen movements produce sparse samples; dense points let the eraser
 * cut strokes precisely.
 */
export function densifyPoints(points: readonly StrokePoint[], maxSpacing: number): StrokePoint[] {
  if (points.length < 2 || maxSpacing <= 0) return points.slice();
  const out: StrokePoint[] = [points[0] as StrokePoint];
  for (let i = 1; i < points.length; i++) {
    const [ax, ay, ap] = points[i - 1] as StrokePoint;
    const b = points[i] as StrokePoint;
    const dist = Math.hypot(b[0] - ax, b[1] - ay);
    const steps = Math.ceil(dist / maxSpacing);
    for (let s = 1; s < steps; s++) {
      const t = s / steps;
      out.push([ax + (b[0] - ax) * t, ay + (b[1] - ay) * t, ap + (b[2] - ap) * t]);
    }
    out.push(b);
  }
  return out;
}

/** Axis-aligned bounds of the point centers. */
export function pointsExtent(points: readonly StrokePoint[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const [x, y] of points) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (minX === Number.POSITIVE_INFINITY) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

// ---------------------------------------------------------------------------
// Base64, for the JSON board file format
// ---------------------------------------------------------------------------

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
